import { useState, useEffect } from 'react';
import { getStatusOptions, updateClient } from '../lib/supabase';
import { changeClientStatus, DMA80, ACTIVE } from '../lib/clientStatus';
import { statusesForPayer, isClosedClient, PAUSED_STATUSES } from '../lib/serviceModel';
import { useAuth } from '../hooks/useAuth';

// Moving to DMA-80 emails the family that their packet is with DCH, so the
// move is blocked until there is an address to send it to.
const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((v || '').trim());

const ELIGIBILITY_GATE_STATUSES = [
  'Intake Form Submitted','Nursing Assessment Pending','Nursing Assessment Completed',
  'PPOT Request Pending','PPOT Request In Progress','Submitted (DMA6A)',
  'Submitted (DMA80)','Approved by GAPP','Active (Receiving Service)',
];

/**
 * `initialStatus` and `initialNotes` let another part of the app open this
 * already filled in — recording a DMA-80 submission knows the stage it implies
 * and every number that belongs in the note. It is still a prefill, not an
 * automatic move: this transition emails the family, so a person confirms it.
 */
export default function StageTransitionModal({ client, onClose, onSuccess, initialStatus = '', initialNotes = '', familyEmailHandled = false }) {
  const { profile } = useAuth();
  const [statuses, setStatuses] = useState([]);
  const [newStatus, setNewStatus] = useState(initialStatus);
  const [notes, setNotes] = useState(initialNotes);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Guardian email captured here when the client record has none — the DMA-80
  // family email has nowhere to go without it.
  const [guardianEmail, setGuardianEmail] = useState(client.guardian_email || '');
  const [savingEmail, setSavingEmail] = useState(false);
  // Moving a child who is receiving care back into an application stage marks
  // them Inactive in AxisCare, where they stop being schedulable. A renewal
  // never needs it — it is recorded on the GAPP Submissions card and the status
  // stays put — so the move has to be confirmed in so many words.
  const [activeConfirmed, setActiveConfirmed] = useState(false);
  // Moving to Active creates the client in AxisCare when they are not there
  // yet. That takes a few seconds and changes another system, so the person
  // is told while it runs and shown what landed before the modal closes.
  const [axisResult, setAxisResult] = useState(null);

  useEffect(() => {
    getStatusOptions().then(({ data }) => setStatuses(data || []));
  }, []);

  const eligibilityBlocked = newStatus && ELIGIBILITY_GATE_STATUSES.includes(newStatus) && client.medicaid_eligible !== 'Yes' && client.medicaid_eligible !== 'Not Applicable';
  const emailBlocked = newStatus === DMA80 && !familyEmailHandled && !isEmail(client.guardian_email);
  const APPLICATION_ZONES = ['Outreach', 'Clinical Intake', 'Authorization'];
  const targetZone = statuses.find((s) => s.status_name === newStatus)?.zone;
  const leavingService = client.current_status === ACTIVE && APPLICATION_ZONES.includes(targetZone);
  const creatingInAxisCare = newStatus === ACTIVE && !client.axiscare_client_id;

  const saveGuardianEmail = async () => {
    if (!isEmail(guardianEmail)) { setError('Enter a valid email address.'); return; }
    setSavingEmail(true); setError('');
    const { error: err } = await updateClient(client.id, { guardian_email: guardianEmail.trim() });
    setSavingEmail(false);
    if (err) { setError(`Could not save the email: ${err.message}`); return; }
    // Keep the guard honest: it reads client.guardian_email, so update the
    // record we were handed rather than waiting for a refetch.
    client.guardian_email = guardianEmail.trim();
    setError('');
  };

  const handleSubmit = async () => {
    if (!newStatus) { setError('Please select a new status.'); return; }
    if (!notes.trim()) { setError('Please add a comment before proceeding.'); return; }
    if (eligibilityBlocked) { setError('Medicaid eligibility must be verified on GAMMIS before moving past Client Contacted.'); return; }
    if (emailBlocked) { setError(`Add ${client.first_name}'s parent or guardian email before submitting the DMA-80 — the family is emailed when the packet goes to DCH.`); return; }
    if (leavingService && !activeConfirmed) { setError(`Confirm the move first — ${client.first_name} is receiving service.`); return; }
    setLoading(true);
    setError('');
    const { error: err, warnings, axiscare } = await changeClientStatus({ client, newStatus, notes, profile, familyEmailHandled });
    if (err) { setError(err.message); setLoading(false); return; }

    onSuccess(newStatus);
    if (creatingInAxisCare && (axiscare || warnings.length)) {
      setLoading(false);
      setAxisResult({ axiscare, warnings });
      return;
    }
    if (warnings.length) window.alert(warnings.join('\n\n'));
    onClose();
  };

  if (axisResult) {
    const { axiscare, warnings } = axisResult;
    const ok = axiscare && ['created', 'linked'].includes(axiscare.outcome);
    return (
      <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && onClose()}>
        <div className="modal" onMouseDown={e => e.stopPropagation()}>
          <div className="modal-header">
            <div className="modal-title">{ok ? `${client.first_name} is set up in AxisCare` : 'Saved — AxisCare needs attention'}</div>
            <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
          </div>
          <div className="modal-body" style={{ fontSize:13, lineHeight:1.6, color:'var(--text2)' }}>
            <p style={{ marginTop:0 }}>{client.last_name}, {client.first_name} is now <b>{ACTIVE}</b> in CAERA.</p>
            {axiscare?.outcome === 'created' && (
              <div style={{ background:'var(--success-bg)', border:'1px solid var(--success)', borderRadius:8, padding:'10px 14px', marginBottom:12 }}>
                <div style={{ fontWeight:600, color:'var(--success)', marginBottom:4 }}>✓ Created in AxisCare — ID {axiscare.axisId}</div>
                Sent: {axiscare.sent.join(', ')}.
              </div>
            )}
            {axiscare?.outcome === 'linked' && (
              <div style={{ background:'var(--success-bg)', border:'1px solid var(--success)', borderRadius:8, padding:'10px 14px', marginBottom:12 }}>
                <div style={{ fontWeight:600, color:'var(--success)' }}>✓ Linked to the client already in AxisCare — ID {axiscare.axisId}</div>
                Same name and date of birth, so no second record was made.
              </div>
            )}
            {axiscare?.skipped?.length > 0 && (
              <div style={{ background:'var(--warn-bg)', border:'1px solid var(--warn)', borderRadius:8, padding:'10px 14px', marginBottom:12 }}>
                AxisCare did not accept: <b>{axiscare.skipped.join(', ')}</b>. Add these in AxisCare by hand.
              </div>
            )}
            {warnings.map((w) => (
              <div key={w} style={{ background:'var(--danger-bg)', border:'1px solid var(--danger)', borderRadius:8, padding:'10px 14px', marginBottom:12, color:'var(--text)' }}>{w}</div>
            ))}
            {ok && <p style={{ marginBottom:0, fontSize:12, color:'var(--text3)' }}>The office has been told in the General Notifier space, and this is recorded in the Activity Log.</p>}
          </div>
          <div className="modal-footer">
            <button className="btn btn-primary" onClick={onClose}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  const zones = ['Outreach','Clinical Intake','Authorization','Active','Inactive'];
  // GAPP authorization stages are offered only to GAPP clients; the database
  // refuses them for any other payer.
  const allowed = statusesForPayer(client.service_type, client.current_status);
  const closing = newStatus && isClosedClient(newStatus) && !isClosedClient(client.current_status);
  const pausing = PAUSED_STATUSES.includes(newStatus);

  return (
    <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" onMouseDown={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="modal-title">Move to next stage</div>
            <div style={{ fontSize:13, color:'var(--text3)', marginTop:2 }}>
              {client.last_name}, {client.first_name} · {client.client_id}
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{ background:'var(--accent-bg)', border:'1px solid var(--border)', borderRadius:8, padding:'10px 14px', marginBottom:16, display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ fontSize:12, color:'var(--text3)' }}>Current:</span>
            <span style={{ fontSize:13, fontWeight:500, color:'var(--text)' }}>{client.current_status}</span>
            <span style={{ marginLeft:'auto', fontSize:11, padding:'2px 8px', borderRadius:10, background: client.medicaid_eligible === 'Yes' ? 'var(--success-bg)' : client.medicaid_eligible === 'No' ? 'var(--danger-bg)' : 'var(--warn-bg)', color: client.medicaid_eligible === 'Yes' ? 'var(--success)' : client.medicaid_eligible === 'No' ? 'var(--danger)' : 'var(--warn)' }}>
              Eligibility: {client.medicaid_eligible || 'Pending'}
            </span>
          </div>

          {emailBlocked && (
            <div style={{ background:'var(--warn-bg)', border:'1px solid var(--warn)', borderRadius:8, padding:'12px 14px', marginBottom:16 }}>
              <div style={{ fontSize:13, color:'var(--warn)', fontWeight:500, marginBottom:4 }}>⚠ No parent or guardian email on file</div>
              <div style={{ fontSize:12.5, color:'var(--text2)', lineHeight:1.6, marginBottom:10 }}>
                Submitting the DMA-80 emails {client.first_name}&rsquo;s family to tell them the packet is with the State. Add the address to continue &mdash; it is saved to the client record.
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <input className="form-input" style={{ flex:1 }} type="email" placeholder="parent@example.com"
                  value={guardianEmail} onChange={e => setGuardianEmail(e.target.value)} />
                <button className="btn btn-primary btn-sm" onClick={saveGuardianEmail} disabled={savingEmail || !isEmail(guardianEmail)}>
                  {savingEmail ? 'Saving…' : 'Save email'}
                </button>
              </div>
            </div>
          )}

          {eligibilityBlocked && (
            <div style={{ background:'var(--danger-bg)', border:'1px solid var(--danger-bg)', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:13, color:'var(--danger)' }}>
              ⚠ Medicaid eligibility must be verified on GAMMIS before moving to this stage. Please update the eligibility check on the DMA tab first.
            </div>
          )}

          <div className="form-group">
            <label className="form-label">New Status <span className="req">*</span></label>
            <select className="form-select" value={newStatus} onChange={e => setNewStatus(e.target.value)}>
              <option value="">Select new status...</option>
              {zones.map(zone => (
                <optgroup key={zone} label={zone}>
                  {statuses.filter(s => s.zone === zone && allowed.includes(s.status_name) && s.status_name !== client.current_status).map(s => (
                    <option key={s.id} value={s.status_name}>{s.status_name}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {leavingService && (
            <div style={{ background:'var(--danger-bg)', border:'1px solid var(--danger)', borderRadius:8, padding:'12px 14px', marginBottom:16, fontSize:12.5, color:'var(--text2)', lineHeight:1.6 }}>
              <div style={{ fontWeight:600, color:'var(--danger)', marginBottom:4 }}>{client.first_name} is receiving service</div>
              Moving {client.first_name} to <b>{newStatus}</b> takes them out of service: they are marked <b>Inactive in AxisCare</b> and can no longer be scheduled there.
              {' '}A <b>renewal</b> does not need this &mdash; record it under Documents &rsaquo; GAPP Submissions and the status stays Active.
              <label style={{ display:'flex', gap:8, alignItems:'flex-start', marginTop:10, color:'var(--text)', fontWeight:500, cursor:'pointer' }}>
                <input type="checkbox" checked={activeConfirmed} onChange={(e) => setActiveConfirmed(e.target.checked)} style={{ marginTop:3 }} />
                I understand {client.first_name} leaves service, and this is not a renewal.
              </label>
            </div>
          )}

          {closing && (
            <div style={{ background:'var(--warn-bg)', border:'1px solid var(--warn-border)', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:12.5, color:'var(--text2)', lineHeight:1.6 }}>
              <div style={{ fontWeight:600, color:'var(--warn)', marginBottom:2 }}>This closes {client.first_name}&rsquo;s record</div>
              {pausing
                ? 'Their schedules are paused and future visits cancelled. Resume the schedules from the Schedule tab when service restarts.'
                : 'Their schedules are ended and future visits cancelled.'}
              {' '}Caregiver assignments end, and they drop off supervision, assessment and authorization monitoring. Open tasks stay open.
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Comment / Notes <span className="req">*</span></label>
            <textarea className="form-textarea" value={notes} onChange={e => setNotes(e.target.value)} placeholder="What happened? What was done? What's the next step?" rows={4} />
          </div>

          <div style={{ background:'var(--accent-bg)', border:'1px solid var(--border)', borderRadius:8, padding:'10px 14px', display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ width:28, height:28, borderRadius:'50%', background:'var(--accent)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:600, color:'#fff', flexShrink:0 }}>
              {profile?.full_name?.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase()}
            </div>
            <div>
              <div style={{ fontSize:13, fontWeight:500 }}>{profile?.full_name}</div>
              <div style={{ fontSize:11, color:'var(--text3)' }}>Signed off — {new Date().toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' })}</div>
            </div>
          </div>

          {creatingInAxisCare && (
            <div style={{ background:'var(--accent-bg)', border:'1px solid var(--border)', borderRadius:8, padding:'10px 14px', marginTop:12, fontSize:12.5, color:'var(--text2)', lineHeight:1.6 }}>
              {loading
                ? <><span className="loading-spinner" style={{ marginRight:8 }} />Creating {client.first_name} in AxisCare… this takes a few seconds.</>
                : <>{client.first_name} is not in AxisCare yet. Saving creates the client there automatically, with everything CAERA holds that AxisCare can take.</>}
            </div>
          )}

          {error && <div className="form-error" style={{ marginTop:12 }}>{error}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={loading || eligibilityBlocked || emailBlocked || (leavingService && !activeConfirmed)}>
            {loading ? <span className="loading-spinner" /> : null}
            Confirm & Save
          </button>
        </div>
      </div>
    </div>
  );
}
